import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";

type SupportTicket = {
  _id: string;
  ticketNumber: string;
  requesterName: string;
  requesterPhone: string;
  requesterEmail?: string;
  category: string;
  priority: string;
  status: string;
  subject: string;
  description: string;
  assignedToUserId?: string;
  assignedToName?: string;
  assignedToEmail?: string;
  assignedToPhotoURL?: string;
  createdAt: string;
  updatedAt: string;
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1/support/tickets`;
const cardsBorderRadii = "8px";

const categoryColorScheme = (category?: string) => {
  switch (category) {
    case "payment":
      return "red";
    case "delivery":
      return "orange";
    case "bookings":
      return "purple";
    case "orders":
      return "blue";
    case "technical":
      return "pink";
    case "account":
      return "cyan";
    default:
      return "gray";
  }
};

const priorityColorScheme = (priority?: string) => {
  switch (priority) {
    case "urgent":
      return "red";
    case "high":
      return "orange";
    case "normal":
      return "blue";
    case "low":
      return "gray";
    default:
      return "gray";
  }
};

const statusColorScheme = (status?: string) => {
  switch (status) {
    case "open":
      return "red";
    case "assigned":
      return "orange";
    case "in_progress":
      return "blue";
    case "waiting_on_customer":
      return "yellow";
    case "resolved":
      return "green";
    case "closed":
      return "gray";
    default:
      return "gray";
  }
};

const FilterChipButton = ({
  active,
  label,
  onClick,
  theme,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  theme: any;
}) => (
  <Button
    size="sm"
    onClick={onClick}
    variant={active ? "solid" : "outline"}
    color={active ? theme.textOnPrimary : theme.text}
    bg={active ? theme.primary : "transparent"}
    borderColor={theme.borderStrong}
    _hover={{ bg: active ? theme.primary : theme.hover }}
    borderRadius={cardsBorderRadii}
  >
    {label}
  </Button>
);

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleString();
};

const SupportTicketsPage: React.FC = () => {
  const { theme } = useCustomTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [statusFilter, setStatusFilter] = useState("open");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [search, setSearch] = useState("");

  const queryParams = useMemo(() => {
    return {
      page,
      limit: 12,
      status: statusFilter === "all" ? "" : statusFilter,
      category: categoryFilter === "all" ? "" : categoryFilter,
      priority: priorityFilter === "all" ? "" : priorityFilter,
      search: search.trim(),
    };
  }, [page, statusFilter, categoryFilter, priorityFilter, search]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_BASE, {
        params: queryParams,
      });

      setTickets(Array.isArray(res.data?.items) ? res.data.items : []);
      setHasMore(Boolean(res.data?.hasMore));
    } catch (error) {
      console.error("Failed to fetch support tickets:", error);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return (
    <Box w="100%" mx="auto" p={{ base: 4, md: 6 }} color={theme.text}>
      <VStack align="stretch" spacing={5}>
        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={{ base: 4, md: 5 }}
          boxShadow={theme.shadowSm}
        >
          <Flex
            direction={{ base: "column", lg: "row" }}
            justify="space-between"
            align={{ base: "start", lg: "center" }}
            gap={4}
          >
            <VStack align="start" spacing={1}>
              <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold">
                Support Tickets
              </Text>
              <Text color={theme.textSubtle}>
                Browse all support requests and open a ticket to manage details.
              </Text>
            </VStack>

            <HStack
              spacing={2}
              flexWrap="wrap"
              alignSelf={{ base: "stretch", lg: "center" }}
            >
              <FilterChipButton
                active={statusFilter === "open"}
                label="Open"
                onClick={() => {
                  setPage(1);
                  setStatusFilter("open");
                }}
                theme={theme}
              />
              <FilterChipButton
                active={statusFilter === "assigned"}
                label="Assigned"
                onClick={() => {
                  setPage(1);
                  setStatusFilter("assigned");
                }}
                theme={theme}
              />
              <FilterChipButton
                active={statusFilter === "in_progress"}
                label="In Progress"
                onClick={() => {
                  setPage(1);
                  setStatusFilter("in_progress");
                }}
                theme={theme}
              />
              <FilterChipButton
                active={statusFilter === "resolved"}
                label="Resolved"
                onClick={() => {
                  setPage(1);
                  setStatusFilter("resolved");
                }}
                theme={theme}
              />
              <FilterChipButton
                active={statusFilter === "all"}
                label="All"
                onClick={() => {
                  setPage(1);
                  setStatusFilter("all");
                }}
                theme={theme}
              />
            </HStack>
          </Flex>
        </Box>

        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={{ base: 4, md: 5 }}
          boxShadow={theme.shadowSm}
        >
          <Stack direction={{ base: "column", xl: "row" }} spacing={3}>
            <Input
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search ticket, requester, assignee, email, phone..."
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
            />

            <Select
              value={categoryFilter}
              onChange={(e) => {
                setPage(1);
                setCategoryFilter(e.target.value);
              }}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
              maxW={{ base: "100%", xl: "220px" }}
            >
              <option value="all">All categories</option>
              <option value="orders">Orders</option>
              <option value="delivery">Delivery</option>
              <option value="bookings">Bookings</option>
              <option value="payment">Payment</option>
              <option value="account">Account</option>
              <option value="technical">Technical</option>
              <option value="general">General</option>
            </Select>

            <Select
              value={priorityFilter}
              onChange={(e) => {
                setPage(1);
                setPriorityFilter(e.target.value);
              }}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
              maxW={{ base: "100%", xl: "200px" }}
            >
              <option value="all">All priorities</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </Select>
          </Stack>
        </Box>

        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          overflow="hidden"
          boxShadow={theme.shadowSm}
        >
          <Box
            display={{ base: "none", lg: "block" }}
            px={5}
            py={3}
            bg={theme.surfaceAlt || theme.surface}
            borderBottom="1px solid"
            borderColor={theme.border}
          >
            <HStack spacing={6} align="center">
              <Box flex="1.4">
                <Text fontSize="sm" fontWeight="bold" color={theme.textSubtle}>
                  Ticket
                </Text>
              </Box>
              <Box flex="1">
                <Text fontSize="sm" fontWeight="bold" color={theme.textSubtle}>
                  Requester
                </Text>
              </Box>
              <Box flex="1">
                <Text fontSize="sm" fontWeight="bold" color={theme.textSubtle}>
                  Category / Priority
                </Text>
              </Box>
              <Box flex="1">
                <Text fontSize="sm" fontWeight="bold" color={theme.textSubtle}>
                  Status / Assignee
                </Text>
              </Box>
              <Box w="170px">
                <Text fontSize="sm" fontWeight="bold" color={theme.textSubtle}>
                  Action
                </Text>
              </Box>
            </HStack>
          </Box>

          {loading ? (
            <Flex align="center" justify="center" py={10} gap={2}>
              <GlobalSpinner />
              <Text color={theme.textSubtle}>Loading support tickets...</Text>
            </Flex>
          ) : tickets.length === 0 ? (
            <Box p={8}>
              <Text textAlign="center" color={theme.textSubtle}>
                No support tickets found.
              </Text>
            </Box>
          ) : (
            tickets.map((ticket) => (
              <Box
                key={ticket._id}
                px={{ base: 4, md: 5 }}
                py={4}
                borderBottom="1px solid"
                borderColor={theme.border}
                _last={{ borderBottom: "none" }}
              >
                <Stack
                  direction={{ base: "column", lg: "row" }}
                  spacing={4}
                  justify="space-between"
                  align={{ base: "start", lg: "center" }}
                >
                  <Box flex="1.4" minW={0}>
                    <Text fontWeight="bold" color={theme.text}>
                      {ticket.ticketNumber}
                    </Text>
                    <Text mt={1} fontWeight="semibold" noOfLines={1}>
                      {ticket.subject}
                    </Text>
                    <Text
                      mt={1}
                      color={theme.textMuted}
                      noOfLines={{ base: 3, lg: 2 }}
                    >
                      {ticket.description}
                    </Text>
                    <Text mt={2} fontSize="sm" color={theme.textSubtle}>
                      Created: {formatDate(ticket.createdAt)}
                    </Text>
                  </Box>

                  <Box flex="1" minW={0}>
                    <Text fontWeight="semibold">{ticket.requesterName}</Text>
                    <Text color={theme.textMuted}>{ticket.requesterPhone}</Text>
                    {ticket.requesterEmail && (
                      <Text color={theme.textMuted} noOfLines={1}>
                        {ticket.requesterEmail}
                      </Text>
                    )}
                  </Box>

                  <Box flex="1" minW={0}>
                    <HStack spacing={2} flexWrap="wrap">
                      <Badge colorScheme={categoryColorScheme(ticket.category)}>
                        {ticket.category}
                      </Badge>
                      <Badge colorScheme={priorityColorScheme(ticket.priority)}>
                        {ticket.priority}
                      </Badge>
                    </HStack>
                  </Box>

                  <Box flex="1" minW={0}>
                    <VStack align="start" spacing={1}>
                      <Badge colorScheme={statusColorScheme(ticket.status)}>
                        {ticket.status}
                      </Badge>
                      {ticket.assignedToUserId ? (
                        <>
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color={theme.text}
                            noOfLines={1}
                          >
                            {ticket.assignedToName ||
                              ticket.assignedToEmail ||
                              ticket.assignedToUserId}
                          </Text>
                          {ticket.assignedToEmail && ticket.assignedToName ? (
                            <Text
                              fontSize="xs"
                              color={theme.textSubtle}
                              noOfLines={1}
                            >
                              {ticket.assignedToEmail}
                            </Text>
                          ) : null}
                        </>
                      ) : (
                        <Text fontSize="sm" color={theme.textSubtle}>
                          Unassigned
                        </Text>
                      )}
                    </VStack>
                  </Box>

                  <Box w={{ base: "100%", lg: "170px" }}>
                    <Button
                      w={{ base: "100%", lg: "auto" }}
                      color={theme.textOnPrimary}
                      bgImage={theme.gradients.primaryButton}
                      _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                      boxShadow={theme.shadowSm}
                      onClick={() => navigate(`/support/tickets/${ticket._id}`)}
                    >
                      View Details
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ))
          )}
        </Box>

        <HStack justify="space-between">
          <Button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            isDisabled={page <= 1 || loading}
            variant="outline"
            borderColor={theme.borderStrong}
            color={theme.text}
            _hover={{ bg: theme.hover }}
          >
            Previous
          </Button>

          <Text color={theme.textSubtle}>Page {page}</Text>

          <Button
            onClick={() => setPage((prev) => prev + 1)}
            isDisabled={!hasMore || loading}
            variant="outline"
            borderColor={theme.borderStrong}
            color={theme.text}
            _hover={{ bg: theme.hover }}
          >
            Next
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default SupportTicketsPage;
